import { useState, useEffect, useContext } from "react";
import { publicFetcher } from "../utils/publicFetcher";
import { AuthContext } from "../context/AuthContext";
import { ACCESS_LEVELS } from "../constants/accessLevels";
import type { OrganisationData } from "../types/organisation";
import type { AccessLevel } from "../types/common";

interface UseOrganisationDataReturn {
  data: OrganisationData | null;
  loading: boolean;
  error: string | null;
  accessLevel: AccessLevel;
  isOwnProfile: boolean;
}

export const useOrganisationData = (organisationId: string): UseOrganisationDataReturn => {
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.currentUser;
  const isAuthenticated = authContext?.isAuthenticated ?? false;
  
  const [data, setData] = useState<OrganisationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [accessLevel, setAccessLevel] = useState<AccessLevel>(ACCESS_LEVELS.PUBLIC);
  
  const isOwnProfile = isAuthenticated && currentUser?.userId === organisationId;

  useEffect(() => {
    const fetchOrganisationData = async () => {
      if (!organisationId) {
        setError("errors.organisationIdRequired");
        setLoading(false);
        return;
      }

      // If viewing own profile, use data from AuthContext (avoid double fetch)
      if (isOwnProfile && currentUser?.userData) {
        setData(currentUser.userData as OrganisationData);
        setAccessLevel(ACCESS_LEVELS.OWN);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await publicFetcher<OrganisationData>({
          url: `/organisation/${organisationId}`,
        });
        
        setData(response);
        
        // Determine access level
        if (isOwnProfile) {
          setAccessLevel(ACCESS_LEVELS.OWN);
        } else if (isAuthenticated) {
          setAccessLevel(ACCESS_LEVELS.AUTHENTICATED);
        } else {
          setAccessLevel(ACCESS_LEVELS.PUBLIC);
        }
      } catch (err) {
        console.error("Error fetching organisation data:", err);
        const errorMessage = err instanceof Error ? err.message : "errors.organisationLoadFailed";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganisationData();
  }, [organisationId, isAuthenticated, currentUser?.userId, currentUser?.userData, isOwnProfile]);

  return { data, loading, error, accessLevel, isOwnProfile };
};

